import { useState, useMemo, useRef, useCallback, useEffect } from "react";
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  getDay,
  addMonths,
  isBefore,
  isAfter,
  isSameDay,
  differenceInDays,
  startOfDay,
  addDays,
} from "date-fns";
import { X, Clock } from "lucide-react";
import { trackEvent } from "@/hooks/use-page-tracking";
import { getCheckInMinNights, midweekWindowsByProximity } from "@shared/minStay";

export type DateRange = { from: Date | undefined; to: Date | undefined };

interface AirbnbDatePickerProps {
  value?: DateRange;
  onSave: (range: DateRange) => void;
  onClose: () => void;
  isDisabled?: (date: Date) => boolean;
  monthsToShow?: number;
  minNights?: number;
  /** When provided, the min-stay notice offers an "ask the hosts" link that
   *  hands the attempted range off to the date-inquiry flow. */
  onInquire?: (range: DateRange) => void;
  /** Dates held by a tentative channel reservation remain unavailable for
   * checkout, but can be tapped to join a backup-interest list. */
  isBackupInterestDate?: (date: Date) => boolean;
  onBackupInterest?: (date: Date) => void;
  /** Cabin-only: apply the seasonal mid-week minimum-stay exception (a 3-night
   *  Mon→Thu stay is allowed in Aug/Sep 2026). When true, the minimum is derived
   *  per check-in date via the shared helper instead of a single fixed number. */
  midweekException?: boolean;
  /** PriceLabs hard-floor minimum for the selected check-in. Unlike the base
   *  `minNights`, this is NEVER reduced by the mid-week exception — PL
   *  per-date restrictions always take precedence over the seasonal rule.
   *  Pass the `minNightsRequired` value from `/api/availability/check` or
   *  `/api/pricing/quote` for the currently-selected (or probed) check-in. */
  plMinNights?: number;
  /** Called whenever the picker's internally-tracked check-in (from) date
   *  changes, including on mount when a pre-selected date is present and when
   *  the user clears the selection. Use this to fire a PriceLabs probe for
   *  that specific check-in so the picker reflects PL minimums during
   *  checkout selection, before the full quote is available. */
  onFromChange?: (date: Date | undefined) => void;
  /** Variant C (price_calendar): per-date nightly prices shown on calendar cells. */
  datePrices?: Map<string, number>;
}

const DAYS = ["S", "M", "T", "W", "T", "F", "S"];

function cabinToday(): Date {
  const ymd = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Chicago",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
  return startOfDay(new Date(`${ymd}T12:00:00`));
}

function rangeHasDisabledDay(
  from: Date,
  to: Date,
  isDisabled: (d: Date) => boolean,
): boolean {
  const innerStart = addDays(from, 1);
  const innerEnd = addDays(to, -1);
  if (!isBefore(innerStart, innerEnd) && !isSameDay(innerStart, innerEnd)) return false;
  const days = eachDayOfInterval({ start: innerStart, end: innerEnd });
  return days.some((d) => isDisabled(d));
}

type DayState =
  | "disabled"
  | "available"
  | "from"
  | "to"
  | "in-range"
  | "past"
  | "too-short"
  | "range-blocked"
  // A valid free date from which the minimum stay can't fit before the next
  // booking. Rendered lighter (no strikethrough — it's not booked), tappable
  // so guests see an explanation rather than a silent dead-end.
  | "orphaned";

function getDayState(
  date: Date,
  from: Date | undefined,
  to: Date | undefined,
  isDisabled: (d: Date) => boolean,
  today: Date,
  selecting: "from" | "to",
  minNights: number,
  firstBlocked: Date | undefined,
  isOrphanedCheckIn?: (d: Date) => boolean,
): DayState {
  if (isBefore(date, today) && !isSameDay(date, today)) return "past";
  // The currently-selected from/to always render their circles — they represent
  // an existing selection, not a new pick, so disabled/orphaned state is irrelevant.
  if (from && isSameDay(date, from)) return "from";
  if (to && isSameDay(date, to)) return "to";
  if (isDisabled(date)) {
    // When picking checkout, allow selecting exactly the first blocked night —
    // guests check out that morning before the next booking's guests arrive.
    // Any night AFTER firstBlocked still becomes "range-blocked" below.
    const isCheckoutOnFirstBlocked =
      selecting === "to" && from && !to && firstBlocked && isSameDay(date, firstBlocked);
    if (!isCheckoutOnFirstBlocked) return "disabled";
    // fall through so the range-blocked / too-short checks run normally
  }
  // When picking a check-in, dates from which a minimum-length stay can't fit
  // before the next booking still appear available (black, no strikethrough) —
  // tapping them triggers the handleDayClick orphan guard which shows an
  // explanation toast. Visually dimming them would imply "booked", which is
  // wrong (they're free nights, just too close to the next booking).
  // The toast pattern mirrors "too-short" checkout dates on the other side.
  //
  // (isOrphanedCheckIn is still called in handleDayClick to gate the tap.)
  if (selecting === "to" && from && !to) {
    // Any checkout on/after the first blocked night after the check-in would
    // make the stay span an unavailable night — mark it not-selectable, the
    // same way Airbnb does, so a guest can never pick a range over a booked
    // night (which used to silently wipe their check-in).
    if (firstBlocked && isAfter(date, firstBlocked)) {
      return "range-blocked";
    }
    // While picking the check-out date, grey out any night that would make the
    // stay shorter than the configured minimum (e.g. "Minimum 2 nights").
    if (minNights > 1 && isAfter(date, from) && differenceInDays(date, from) < minNights) {
      return "too-short";
    }
  }
  if (from && to && isAfter(date, from) && isBefore(date, to)) return "in-range";
  return "available";
}

function MonthGrid({
  month,
  from,
  to,
  isDisabled,
  today,
  selecting,
  minNights,
  firstBlocked,
  attempted,
  onDayClick,
  isOrphanedCheckIn,
  datePrices,
  isBackupInterestDate,
  onBackupInterest,
}: {
  month: Date;
  from: Date | undefined;
  to: Date | undefined;
  isDisabled: (d: Date) => boolean;
  today: Date;
  selecting: "from" | "to";
  minNights: number;
  firstBlocked: Date | undefined;
  attempted: Date | undefined;
  onDayClick: (d: Date) => void;
  isOrphanedCheckIn?: (d: Date) => boolean;
  /** Variant C (price_calendar): per-date nightly prices shown below the date number. */
  datePrices?: Map<string, number>;
  isBackupInterestDate?: (date: Date) => boolean;
  onBackupInterest?: (date: Date) => void;
}) {
  const days = eachDayOfInterval({ start: startOfMonth(month), end: endOfMonth(month) });
  const startPad = getDay(startOfMonth(month));
  // Show the "N-night minimum" tag above the check-in date while the guest is
  // still choosing a checkout — mirrors Airbnb so short gaps read as a rule,
  // not as unavailable dates.
  const showMinPill = selecting === "to" && !!from && !to && minNights > 1;

  return (
    <div className="mb-7">
      <p className="text-base font-bold text-foreground text-center mb-3 font-serif">
        {format(month, "MMMM yyyy")}
      </p>
      <div className="grid grid-cols-7 mb-1">
        {DAYS.map((d, i) => (
          <div key={i} className="text-center text-xs font-semibold text-muted-foreground py-1">
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {Array.from({ length: startPad }).map((_, i) => (
          <div key={`pad-${i}`} className={datePrices ? "h-[52px]" : "h-11"} />
        ))}
        {days.map((day) => {
          const state = getDayState(day, from, to, isDisabled, today, selecting, minNights, firstBlocked, isOrphanedCheckIn);
          // Too-short and orphaned dates stay tappable: tapping them explains
          // why instead of silently doing nothing. Truly unavailable dates
          // (past / booked / blocked) and range-blocked checkouts are inert.
          const isUnavailable = state === "past" || state === "disabled" || state === "range-blocked";
          const acceptsBackupInterest =
            state === "disabled" && !!onBackupInterest && !!isBackupInterestDate?.(day);
          const isAttempted = !!attempted && isSameDay(day, attempted);

          const bandClasses = (() => {
            if (!from) return "";
            if (state === "from" && to) return "absolute top-0 bottom-0 left-1/2 right-0 bg-accent/60";
            if (state === "to") return "absolute top-0 bottom-0 left-0 right-1/2 bg-accent/60";
            if (state === "in-range") return "absolute inset-0 bg-accent/60";
            return "";
          })();

          const circleClasses = (() => {
            if (state === "from" || state === "to")
              return "relative z-10 w-10 h-10 flex items-center justify-center rounded-full bg-primary text-primary-foreground font-semibold text-sm select-none";
            if (state === "in-range")
              return "relative z-10 w-10 h-10 flex items-center justify-center text-sm font-medium text-foreground select-none";
            if (isAttempted)
              return "relative z-10 w-10 h-10 flex items-center justify-center rounded-full border-2 border-foreground text-sm font-semibold text-foreground select-none";
            if (isUnavailable)
              return "relative z-10 w-10 h-10 flex items-center justify-center text-sm text-muted-foreground/40 line-through select-none";
            return "relative z-10 w-10 h-10 flex items-center justify-center text-sm font-medium text-foreground select-none hover:bg-muted/60 rounded-full transition-colors duration-100";
          })();

          // Column-aware alignment so the pill never spills outside the grid.
          const pillPosition = (() => {
            const col = getDay(day);
            if (col <= 1) return "left-0";
            if (col >= 5) return "right-0";
            return "left-1/2 -translate-x-1/2";
          })();

          // Variant C: nightly price label shown below the date number.
          const priceLabel = (() => {
            if (!datePrices || isUnavailable) return null;
            const price = datePrices.get(format(day, "yyyy-MM-dd"));
            if (price == null) return null;
            return price >= 1000 ? `$${(price / 1000).toFixed(1)}k` : `$${price}`;
          })();

          return (
            <div
              key={day.toISOString()}
              className={`relative ${datePrices ? "h-[52px]" : "h-11"} flex items-center justify-center ${isUnavailable && !acceptsBackupInterest ? "cursor-not-allowed" : "cursor-pointer"}`}
              onClick={() => {
                if (acceptsBackupInterest) onBackupInterest?.(day);
                else if (!isUnavailable) onDayClick(day);
              }}
              data-testid={`airbnb-day-${format(day, "yyyy-MM-dd")}`}
              aria-label={acceptsBackupInterest ? `${format(day, "MMMM d, yyyy")} — tentative hold, join backup list` : undefined}
            >
              {bandClasses && <div className={bandClasses} />}
              {showMinPill && state === "from" && (
                <div
                  className={`absolute -top-5 ${pillPosition} z-20 whitespace-nowrap rounded-full bg-foreground text-background text-[11px] font-semibold px-2.5 py-1 shadow-md pointer-events-none`}
                  data-testid="min-stay-pill"
                >
                  {minNights}-night minimum
                </div>
              )}
              {priceLabel ? (
                <div className="relative z-10 flex flex-col items-center justify-center select-none gap-0.5">
                  <div className={circleClasses}>{format(day, "d")}</div>
                  <span className="text-[9px] leading-none text-muted-foreground tabular-nums">{priceLabel}</span>
                </div>
              ) : (
                <div className="relative">
                  <div className={acceptsBackupInterest ? "relative z-10 w-10 h-10 flex items-center justify-center rounded-full border border-dashed border-amber-500 bg-amber-50 text-sm font-semibold text-amber-800 select-none" : circleClasses}>
                    {format(day, "d")}
                  </div>
                  {acceptsBackupInterest && <span className="absolute -bottom-1 left-1/2 h-1.5 w-1.5 -translate-x-1/2 rounded-full bg-amber-500" />}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function AirbnbDatePicker({
  value,
  onSave,
  onClose,
  isDisabled = () => false,
  monthsToShow = 6,
  minNights = 1,
  onInquire,
  midweekException = false,
  plMinNights = 0,
  onFromChange,
  datePrices,
  isBackupInterestDate,
  onBackupInterest,
}: AirbnbDatePickerProps) {
  const effectiveMinNights = Math.max(1, minNights);
  // PriceLabs hard floor: the effective base for all non-mid-week paths.
  // Mid-week exception can only REDUCE the base setting, never the PL floor.
  const hardFloorMin = Math.max(effectiveMinNights, plMinNights);
  const today = useMemo(() => cabinToday(), []);
  const [from, setFrom] = useState<Date | undefined>(value?.from);
  const [to, setTo] = useState<Date | undefined>(value?.to);
  const [selecting, setSelecting] = useState<"from" | "to">(
    value?.from && !value?.to ? "to" : "from"
  );

  // Notify the parent whenever the check-in date changes (including on mount
  // when a pre-selected date is present) so it can fire a PL probe and pass
  // back plMinNights for the right check-in before checkout is chosen.
  useEffect(() => {
    onFromChange?.(from);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from]);
  // The checkout date the guest just tapped that couldn't be selected: outlines
  // that date and shows a notice (min-stay or blocked-night) until it
  // auto-dismisses or the selection changes.
  const [attempted, setAttempted] = useState<Date | undefined>(undefined);
  const [attemptedReason, setAttemptedReason] = useState<"min-stay" | "blocked" | "orphaned">("min-stay");
  // Attempted-stay lengths we've already logged this mount, so repeated taps on
  // a too-short date don't spam duplicate "tried N nights" story beats.
  const attemptedNightsRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    if (!attempted) return;
    const t = setTimeout(() => setAttempted(undefined), 8000);
    return () => clearTimeout(t);
  }, [attempted]);

  // A month is worth showing only if a guest could actually START a stay in it:
  // at least one date that isn't past/blocked AND from which a full
  // minimum-length stay (mid-week exception aware) fits without spanning an
  // unavailable night. A lone open day like a Thursday the 30th — where the next
  // nights are already booked — is NOT a bookable check-in.
  //
  // IMPORTANT: intentionally does NOT include plMinNights / hardFloorMin.
  // Those come from an async PriceLabs probe that fires when the guest taps a
  // check-in date (onFromChange). If plMinNights were in the deps, the months
  // array would recompute when the probe returns, potentially dropping the
  // leading month from the rendered list and causing the scroll container to
  // visually snap — the pixel position stays the same but the content above the
  // viewport shrinks, making it look like an aggressive calendar jump.
  // The PL floor still applies correctly via activeMinNights (which DOES include
  // plMinNights) for greying out too-short checkout dates; the leading-month
  // trim just uses the base minimum stay instead.
  const monthHasBookableCheckIn = useCallback(
    (month: Date): boolean => {
      const days = eachDayOfInterval({ start: startOfMonth(month), end: endOfMonth(month) });
      return days.some((d) => {
        if (isBefore(d, today) && !isSameDay(d, today)) return false;
        if (isDisabled(d)) return false;
        const min = midweekException
          ? getCheckInMinNights(d, effectiveMinNights)
          : effectiveMinNights;
        for (let i = 0; i < min; i++) {
          const night = addDays(d, i);
          if (isBefore(night, today) && !isSameDay(night, today)) return false;
          if (isDisabled(night)) return false;
        }
        return true;
      });
    },
    [today, isDisabled, midweekException, effectiveMinNights],
  );

  // Per-day version of monthHasBookableCheckIn: returns true when no qualifying
  // stay can start on this date (gap too short to reach the minimum). Used to
  // grey out orphaned pockets — e.g. Jul 16–18 free before a Jul-19 booking
  // when min-stay is 4 nights. Only applied while picking a check-in (selecting
  // === "from"); checkout date selection uses the raw isDisabled check.
  const isOrphanedCheckIn = useCallback(
    (date: Date): boolean => {
      if (isDisabled(date)) return false; // already handled by isDisabled
      // PL floor: same rule — mid-week exception can't reduce below PL minimum.
      const min = midweekException
        ? Math.max(getCheckInMinNights(date, effectiveMinNights), plMinNights)
        : hardFloorMin;
      for (let i = 0; i < min; i++) {
        if (isDisabled(addDays(date, i))) return true;
      }
      return false;
    },
    [isDisabled, midweekException, effectiveMinNights, plMinNights, hardFloorMin],
  );

  const months = useMemo(() => {
    const all = Array.from({ length: monthsToShow }, (_, i) => addMonths(startOfMonth(today), i));
    // Drop LEADING months that have no bookable check-in (e.g. the current month
    // is fully booked/blocked), so the calendar opens on the first month a guest
    // can actually book instead of a wall of struck-through dates. Only trims the
    // front — later fully-booked months stay for calendar continuity.
    const firstBookable = all.findIndex((m) => monthHasBookableCheckIn(m));
    return firstBookable > 0 ? all.slice(firstBookable) : all;
  }, [today, monthsToShow, monthHasBookableCheckIn]);

  // The minimum that applies to the CURRENT check-in. With the cabin's seasonal
  // mid-week exception, a Monday check-in whose Mon/Tue/Wed nights all fall in
  // Aug–Sep 2026 drops to a 3-night minimum; every other check-in keeps the base
  // minimum. Drives the greyed-out too-short checkouts, the min-stay pill, and
  // the subtitle so a qualifying Thursday checkout becomes selectable.
  // The PL floor is a hard constraint that the mid-week exception cannot
  // reduce. A qualifying Mon check-in in Aug/Sep 2026 drops the base 4-night
  // minimum to 3, but if PL says 5 nights for that date, activeMinNights=5.
  const activeMinNights = useMemo(
    () =>
      midweekException && from
        ? Math.max(getCheckInMinNights(from, effectiveMinNights), plMinNights)
        : hardFloorMin,
    [midweekException, from, effectiveMinNights, plMinNights, hardFloorMin],
  );

  // When a guest taps a checkout that's too short during the mid-week season,
  // find the nearest AVAILABLE 3-night Mon→Thu window to their check-in so we can
  // offer a one-tap "select these dates" suggestion instead of a dead-end notice.
  // Candidate windows come from the shared date math; availability (blocked/past
  // nights) is filtered here against the picker's own isDisabled knowledge.
  const suggestedMidweek = useMemo(() => {
    if (!midweekException || !attempted || attemptedReason !== "min-stay" || !from) {
      return undefined;
    }
    const parseIso = (iso: string) => {
      const [y, m, d] = iso.split("-").map(Number);
      return new Date(y, m - 1, d);
    };
    for (const w of midweekWindowsByProximity(from)) {
      const ci = parseIso(w.checkIn);
      const co = parseIso(w.checkOut);
      if (isBefore(ci, today)) continue; // window already started
      if (isDisabled(ci)) continue; // check-in night unavailable
      if (rangeHasDisabledDay(ci, co, isDisabled)) continue; // Tue/Wed night blocked
      return { from: ci, to: co };
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [midweekException, attempted, attemptedReason, from, isDisabled, today]);

  const applyMidweekSuggestion = () => {
    if (!suggestedMidweek) return;
    setFrom(suggestedMidweek.from);
    setTo(suggestedMidweek.to);
    setSelecting("from");
    setAttempted(undefined);
    trackEvent("midweek_suggestion_applied", {
      checkIn: format(suggestedMidweek.from, "yyyy-MM-dd"),
      checkOut: format(suggestedMidweek.to, "yyyy-MM-dd"),
    });
  };

  // While the guest is choosing a checkout, find the first blocked/unavailable
  // night after their check-in. Any checkout on/after this night would span an
  // unavailable night, so the calendar greys those dates out (see getDayState).
  const firstBlockedAfterFrom = useMemo(() => {
    if (selecting !== "to" || !from || to) return undefined;
    const rangeEnd = endOfMonth(months[months.length - 1]);
    let d = addDays(from, 1);
    while (!isAfter(d, rangeEnd)) {
      if (isDisabled(d)) return d;
      d = addDays(d, 1);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selecting, from, to, months, isDisabled]);

  const handleDayClick = (day: Date) => {
    setAttempted(undefined);
    if (selecting === "from") {
      // Orphaned check-in: the date is free but the minimum stay can't fit
      // before the next booking. Show an explanation rather than proceeding.
      if (isOrphanedCheckIn?.(day)) {
        setAttempted(day);
        setAttemptedReason("orphaned");
        return;
      }
      setFrom(day);
      setTo(undefined);
      setSelecting("to");
    } else {
      if (!from || isSameDay(day, from)) {
        setFrom(day);
        setTo(undefined);
        setSelecting("to");
        return;
      }
      if (isBefore(day, from)) {
        // When the tapped date is before the current check-in, normally we'd
        // restart with this date as the new check-in. But if it's orphaned,
        // show an explanation instead — silently hijacking the check-in with
        // a date that can't actually start a stay would just confuse the guest.
        if (isOrphanedCheckIn?.(day)) {
          setAttempted(day);
          setAttemptedReason("orphaned");
          return;
        }
        setFrom(day);
        setTo(undefined);
        setSelecting("to");
        return;
      }
      // The tapped checkout would span an already-booked/blocked night. Keep the
      // check-in and explain, instead of silently discarding it and restarting
      // the selection at the tapped date. (Normally these dates aren't even
      // selectable — see the "range-blocked" state — this is the safety net.)
      if (rangeHasDisabledDay(from, day, isDisabled)) {
        setAttempted(day);
        setAttemptedReason("blocked");
        return;
      }
      // Enforce minimum stay: keep the check-in, outline the tapped date, and
      // explain the rule instead of silently restarting the selection.
      if (differenceInDays(day, from) < activeMinNights) {
        // Story beat: the guest "tried" a stay shorter than the minimum. Log it
        // (deduped per attempted length) so session replays show the intent —
        // e.g. "Tried 3 nights — below 4-night minimum" — even though it never
        // became a real selection.
        const attemptedNights = differenceInDays(day, from);
        if (attemptedNights > 0 && !attemptedNightsRef.current.has(attemptedNights)) {
          attemptedNightsRef.current.add(attemptedNights);
          trackEvent("nights_attempted", { nights: attemptedNights, minNights: activeMinNights });
        }
        setAttempted(day);
        setAttemptedReason("min-stay");
        return;
      }
      setTo(day);
      setSelecting("from");
    }
  };

  const handleClear = () => {
    setFrom(undefined);
    setTo(undefined);
    setSelecting("from");
    setAttempted(undefined);
  };

  const handleClose = () => {
    onSave({ from, to });
    onClose();
  };

  const nights = from && to ? differenceInDays(to, from) : 0;

  const title = from && to
    ? `${nights} night${nights !== 1 ? "s" : ""}`
    : from
    ? "Select checkout date"
    : "Select dates";

  const subtitle = activeMinNights > 1
    ? `Minimum stay: ${activeMinNights} nights`
    : from && to
    ? `${format(from, "MMM d")} – ${format(to, "MMM d")}`
    : "Add your travel dates";

  // Swipe-to-close: downward swipe on the HEADER dismisses picker and saves dates.
  // Only fires when the scroll container is at the top so normal calendar scrolling
  // is never intercepted.
  const scrollRef = useRef<HTMLDivElement>(null);
  const touchStartY = useRef<number | null>(null);
  const touchStartX = useRef<number | null>(null);
  const touchStartTime = useRef<number>(0);
  const onTouchStart = useCallback((e: React.TouchEvent) => {
    const t = e.touches[0];
    touchStartY.current = t.clientY;
    touchStartX.current = t.clientX;
    touchStartTime.current = Date.now();
  }, []);
  const onTouchEnd = useCallback((e: React.TouchEvent) => {
    const t = e.changedTouches[0];
    const startY = touchStartY.current;
    const startX = touchStartX.current;
    if (startY == null || startX == null) return;
    const dy = t.clientY - startY;
    const dx = Math.abs(t.clientX - startX);
    const elapsed = Date.now() - touchStartTime.current;
    const scrollTop = scrollRef.current?.scrollTop ?? 0;
    // Only dismiss if: fast enough, clearly vertical, big enough, AND scroll is at top
    if (dy > 80 && dy > dx && elapsed < 400 && scrollTop === 0) {
      handleClose();
    }
    touchStartY.current = null;
    touchStartX.current = null;
  }, [from, to]);

  return (
    <div
      className="relative flex flex-col h-full bg-background"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div className="shrink-0 border-b border-border/60 px-4 sm:px-6 pt-4 sm:pt-5 pb-4">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="min-w-0">
            <p className="font-serif text-xl font-bold text-foreground tracking-tight">
              {title}
            </p>
            <p
              className="text-sm text-muted-foreground mt-0.5"
              data-testid={effectiveMinNights > 1 ? "airbnb-picker-min-nights" : undefined}
            >
              {subtitle}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="-mt-1 -mr-1 w-9 h-9 shrink-0 flex items-center justify-center rounded-full hover:bg-muted transition-colors duration-150"
            data-testid="airbnb-picker-close"
            aria-label="Close"
          >
            <X className="w-4 h-4 text-foreground" />
          </button>
        </div>

        {/* Check-in / Check-out pills — shows which date you're picking next */}
        <div className="grid grid-cols-2 rounded-xl border border-input overflow-hidden">
          <button
            type="button"
            onClick={() => setSelecting("from")}
            className={`p-3 text-left transition-colors duration-150 ${
              selecting === "from"
                ? "relative z-10 ring-2 ring-foreground ring-inset rounded-xl"
                : "border-r border-input hover:bg-muted/40"
            }`}
            data-testid="airbnb-pill-checkin"
          >
            <span className="block text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
              Check-in
            </span>
            <span className={`block text-sm font-semibold mt-0.5 ${from ? "text-foreground" : "text-muted-foreground"}`}>
              {from ? format(from, "MMM d, yyyy") : "Add date"}
            </span>
          </button>
          <button
            type="button"
            onClick={() => setSelecting(from ? "to" : "from")}
            className={`p-3 text-left transition-colors duration-150 ${
              selecting === "to"
                ? "relative z-10 ring-2 ring-foreground ring-inset rounded-xl"
                : "hover:bg-muted/40"
            }`}
            data-testid="airbnb-pill-checkout"
          >
            <span className="block text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
              Checkout
            </span>
            <span className={`block text-sm font-semibold mt-0.5 ${to ? "text-foreground" : "text-muted-foreground"}`}>
              {to ? format(to, "MMM d, yyyy") : "Add date"}
            </span>
          </button>
        </div>
      </div>

      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto overscroll-contain px-4 sm:px-6 pt-5"
        style={{ WebkitOverflowScrolling: "touch" } as React.CSSProperties}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
          {months.map((month) => (
            <MonthGrid
              key={month.toISOString()}
              month={month}
              from={from}
              to={to}
              isDisabled={isDisabled}
              today={today}
              selecting={selecting}
              minNights={activeMinNights}
              firstBlocked={firstBlockedAfterFrom}
              attempted={attempted}
              onDayClick={handleDayClick}
              isOrphanedCheckIn={isOrphanedCheckIn}
              datePrices={datePrices}
              isBackupInterestDate={isBackupInterestDate}
              onBackupInterest={onBackupInterest}
            />
          ))}
        </div>
        {datePrices && (
          <p className="text-[11px] text-muted-foreground text-center pt-1 pb-2 px-4">
            Average nightly rate — based on your stay length
          </p>
        )}
      </div>

      {/* Selection notice — pops up when the guest taps a checkout date that
          can't be selected (too close to check-in, or spanning a booked night),
          keeping their check-in and explaining why, mirroring Airbnb. */}
      {attempted && (
        <div
          className="absolute bottom-16 left-3 right-3 z-30 rounded-2xl border border-border bg-background shadow-xl px-4 py-3.5"
          data-testid="notice-min-stay"
        >
          <div className="flex items-start gap-3">
            <Clock className="w-4 h-4 mt-0.5 shrink-0 text-foreground" />
            <div className="min-w-0 flex-1">
              {attemptedReason === "orphaned" ? (
                <>
                  <p className="text-sm font-semibold text-foreground">
                    Not enough room for {activeMinNights} nights
                  </p>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    The next booking starts before a {activeMinNights}-night stay
                    would end from this date. Try a nearby check-in with more
                    room — the lighter dates above are available.
                  </p>
                </>
              ) : attemptedReason === "blocked" ? (
                <>
                  <p className="text-sm font-semibold text-foreground">
                    Those dates aren't available
                  </p>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    {from ? `Your check-in on ${format(from, "MMM d")} is still selected. ` : ""}
                    There's an already-booked night before that checkout — pick an
                    earlier checkout date.
                  </p>
                </>
              ) : suggestedMidweek ? (
                <>
                  <p className="text-sm font-semibold text-foreground">
                    Try a 3-night mid-week stay
                  </p>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    That's shorter than our {activeMinNights}-night minimum — but in August &amp; September you can book 3 nights midweek. Nearest opening:{" "}
                    <span className="font-medium text-foreground">
                      {format(suggestedMidweek.from, "EEE, MMM d")} – {format(suggestedMidweek.to, "EEE, MMM d")}
                    </span>
                    .
                  </p>
                  <button
                    type="button"
                    onClick={applyMidweekSuggestion}
                    className="mt-2 text-sm font-semibold text-foreground underline underline-offset-4 hover:text-foreground/70 transition-colors duration-150"
                    data-testid="link-midweek-suggestion"
                  >
                    Select these dates
                  </button>
                </>
              ) : onInquire ? (
                <>
                  <p className="text-sm font-semibold text-foreground">
                    Want to stay fewer than {activeMinNights} nights?
                  </p>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    Our usual minimum is {activeMinNights} nights, but shorter stays are sometimes possible.
                  </p>
                  <button
                    type="button"
                    onClick={() => onInquire({ from, to: attempted })}
                    className="mt-2 text-sm font-semibold text-foreground underline underline-offset-4 hover:text-foreground/70 transition-colors duration-150"
                    data-testid="link-min-stay-inquire"
                  >
                    Inquire with your host about a shorter stay
                  </button>
                </>
              ) : (
                <>
                  <p className="text-sm font-semibold text-foreground">
                    Minimum stay: {activeMinNights} nights
                  </p>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    This date does not meet minimum stay requirements.
                  </p>
                </>
              )}
            </div>
            <button
              type="button"
              onClick={() => setAttempted(undefined)}
              className="-mt-1 -mr-1 w-8 h-8 shrink-0 flex items-center justify-center rounded-full hover:bg-muted transition-colors duration-150"
              data-testid="notice-min-stay-close"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4 text-foreground" />
            </button>
          </div>
        </div>
      )}

      <div className="shrink-0 flex items-center justify-between px-4 sm:px-6 py-3 border-t border-border/60 bg-background">
        <button
          onClick={handleClear}
          className="text-sm font-semibold text-foreground underline underline-offset-4 hover:text-foreground/70 transition-colors duration-150"
          data-testid="airbnb-picker-clear"
        >
          Clear dates
        </button>
        <button
          onClick={handleClose}
          className="rounded-lg px-7 py-2.5 text-sm font-semibold text-primary-foreground bg-primary hover:opacity-90 active:opacity-95 transition-opacity duration-150 shadow-sm"
          data-testid="airbnb-picker-close-cta"
        >
          {from && to ? "Confirm dates" : "Done"}
        </button>
      </div>
    </div>
  );
}
